import {CheckOutlined} from '@ant-design/icons';
import {Button, Divider, Input, InputRef, Select, SelectProps} from 'antd';
import {NoFormStyle} from 'antd/es/form/context';
import type {LabeledValue} from 'antd/es/select';
import type {BaseSelectRef} from 'rc-select';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {eachWordCap} from '../../../utils/FormUtils/FirstCap';

interface OptionShape {
  value: string | number;
  label: string;
}

export const OTHER_VALUE_PREFIX = 'other_';
const OTHER_VALUE_PATTERN = /^other_(.+)$/;
/** Sentinel list value for the “Other” row; never persisted via onChange. */
export const OTHER_OPTION_SENTINEL = '__bc_select_other__';

function isSentinelItem(x: unknown, labelInValue?: boolean): boolean {
  if (labelInValue && x && typeof x === 'object' && 'value' in x) {
    return String((x as LabeledValue).value) === OTHER_OPTION_SENTINEL;
  }

  return String(x) === OTHER_OPTION_SENTINEL;
}

function isOtherSentinelSelectValue(
  v: SelectProps['value'],
  labelInValue?: boolean
): boolean {
  if (v === undefined || v === null) {
    return false;
  }

  if (Array.isArray(v)) {
    return v.some((x) => isSentinelItem(x, labelInValue));
  }

  return isSentinelItem(v, labelInValue);
}

/** Removes the internal “Other” sentinel from multi/tags values only. */
function stripOtherSentinelFromArray(
  v: SelectProps['value'],
  labelInValue?: boolean
): SelectProps['value'] {
  if (!Array.isArray(v)) {
    return v;
  }

  return v.filter((x) => !isSentinelItem(x, labelInValue));
}

function selectValuesShallowEqual(
  a: SelectProps['value'],
  b: SelectProps['value'],
  labelInValue?: boolean
): boolean {
  if (Object.is(a, b)) {
    return true;
  }

  if (a === undefined && b === undefined) {
    return true;
  }

  if (a === null && b === null) {
    return true;
  }

  if (a == null || b == null) {
    return false;
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }

    return a.every((item, i) =>
      selectValuesShallowEqual(item, b[i], labelInValue)
    );
  }

  if (
    labelInValue &&
    a &&
    b &&
    typeof a === 'object' &&
    typeof b === 'object' &&
    'value' in a &&
    'value' in b
  ) {
    return (a as LabeledValue).value === (b as LabeledValue).value;
  }

  return a === b;
}

export function labelFromOtherStored(stored: string): string {
  const m = stored.match(OTHER_VALUE_PATTERN);

  return (m?.[1] ?? stored).trim();
}

/** Collect stored `other_*` string values from Select `value` (single or multiple, with or without labelInValue). */
function collectOtherStoredValues(
  value: SelectProps['value'],
  labelInValue?: boolean
): string[] {
  const out: string[] = [];
  const pushIfOther = (v: unknown) => {
    if (typeof v === 'string' && OTHER_VALUE_PATTERN.test(v)) {
      out.push(v);
    }
  };

  if (value === undefined || value === null) {
    return out;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      if (labelInValue && item && typeof item === 'object' && 'value' in item) {
        pushIfOther((item as LabeledValue).value);
      } else {
        pushIfOther(item);
      }
    }

    return out;
  }

  if (labelInValue && typeof value === 'object' && 'value' in value) {
    pushIfOther((value as LabeledValue).value);
  } else {
    pushIfOther(value);
  }

  return out;
}

function normalizeBaseOptions(raw: SelectProps['options']): OptionShape[] {
  return [...((raw ?? []) as OptionShape[])];
}

/** Ant Design clear often emits `undefined`; forms/API need explicit empty values. */
function normalizeClearedSelectValue(
  nextValue: SelectProps['value'],
  mode?: SelectProps['mode']
): SelectProps['value'] {
  const isMultiple = mode === 'multiple' || mode === 'tags';
  const isEmptyArray = Array.isArray(nextValue) && nextValue.length === 0;

  if (
    nextValue === undefined ||
    nextValue === null ||
    (isMultiple && isEmptyArray)
  ) {
    return isMultiple ? [] : null;
  }

  return nextValue;
}

function resolveEditingOtherStored(
  value: SelectProps['value'],
  lifecycle: OptionShape[],
  labelInValue?: boolean,
  isMultiple?: boolean
): string | undefined {
  const fromValue = collectOtherStoredValues(value, labelInValue);
  if (fromValue.length > 0) {
    return isMultiple ? fromValue[fromValue.length - 1] : fromValue[0];
  }

  if (lifecycle.length > 0) {
    return String(lifecycle[lifecycle.length - 1].value);
  }

  return undefined;
}

export interface SelectWithOtherOptionProps
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extends SelectProps<any> {
  /** Max length for the “Specify other…” footer input. Defaults to 100. */
  maxLength?: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SelectWithOtherOption: React.FC<SelectWithOtherOptionProps> = (props) => {
  const {
    onChange,
    labelInValue,
    mode,
    value,
    open: openFromParent,
    defaultOpen,
    onDropdownVisibleChange,
    maxLength = 100,
    ...restProps
  } = props;

  const openControlledByParent = openFromParent !== undefined;
  const [internalOpen, setInternalOpen] = useState(!!defaultOpen);
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherInputDraft, setOtherInputDraft] = useState('');
  const [otherInputMaxLengthError, setOtherInputMaxLengthError] = useState(false);
  const mergedOpen = openControlledByParent
    ? openFromParent
    : internalOpen || showOtherInput;

  const inputRef = useRef<InputRef>(null);
  const selectRef = useRef<BaseSelectRef>(null);
  const valueRef = useRef(value);
  const lifecycleOtherOptionsRef = useRef<OptionShape[]>([]);
  /** `other_*` value being edited in the footer (if any), cleared after confirm. */
  const editingOtherStoredRef = useRef<string | undefined>(undefined);
  /** Skip the close that follows choosing the internal “Other” row so the footer stays open. */
  const ignoreNextDropdownCloseRef = useRef(false);

  const handleDropdownVisibleChange = useCallback(
    (next: boolean) => {
      onDropdownVisibleChange?.(next);
      if (!next && ignoreNextDropdownCloseRef.current) {
        ignoreNextDropdownCloseRef.current = false;
        if (!openControlledByParent) {
          queueMicrotask(() => {
            setInternalOpen(true);
          });
        }

        return;
      }
      if (!next) {
        setShowOtherInput(false);
        setOtherInputDraft('');
        setOtherInputMaxLengthError(false);
      }
      if (!openControlledByParent) {
        setInternalOpen(next);
      }
    },
    [onDropdownVisibleChange, openControlledByParent]
  );

  const closeDropdown = useCallback(() => {
    handleDropdownVisibleChange(false);
  }, [handleDropdownVisibleChange]);

  /** Custom `other_*` options created or selected during this mount — kept in the list even when value changes. */
  const [lifecycleOtherOptions, setLifecycleOtherOptions] = useState<
    OptionShape[]
  >([]);

  const [options, setOptions] = useState<OptionShape[]>(() =>
    normalizeBaseOptions(props.options)
  );

  const setLifecycleOtherOption = useCallback(
    (opt: OptionShape, replaceStored?: string) => {
      const isMultiple = mode === 'multiple' || mode === 'tags';
      setLifecycleOtherOptions((prev) => {
        if (!isMultiple) {
          return [opt];
        }

        if (replaceStored) {
          const next = prev.filter((o) => String(o.value) !== replaceStored);
          if (next.some((o) => String(o.value) === String(opt.value))) {
            return next;
          }

          return [...next, opt];
        }

        return prev.some((o) => String(o.value) === String(opt.value))
          ? prev
          : [...prev, opt];
      });
    },
    [mode]
  );

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    lifecycleOtherOptionsRef.current = lifecycleOtherOptions;
  }, [lifecycleOtherOptions]);

  useEffect(() => {
    const fromValue = collectOtherStoredValues(value, labelInValue);
    if (fromValue.length === 0) {
      return;
    }

    const isMultiple = mode === 'multiple' || mode === 'tags';

    setLifecycleOtherOptions((prev) => {
      if (!isMultiple) {
        const stored = fromValue[0];
        const opt = {value: stored, label: labelFromOtherStored(stored)};
        if (
          prev.length === 1 &&
          String(prev[0].value) === stored &&
          prev[0].label === opt.label
        ) {
          return prev;
        }

        return [opt];
      }

      const seen = new Set(prev.map((o) => String(o.value)));
      let changed = false;
      const next = [...prev];

      for (const stored of fromValue) {
        if (!seen.has(stored)) {
          next.push({value: stored, label: labelFromOtherStored(stored)});
          seen.add(stored);
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [value, labelInValue, mode]);

  useEffect(() => {
    const base = normalizeBaseOptions(props.options);
    const baseValueSet = new Set(base.map((o) => String(o.value)));
    const extras: OptionShape[] = [];

    const addExtra = (opt: OptionShape) => {
      const key = String(opt.value);
      if (!baseValueSet.has(key)) {
        extras.push(opt);
        baseValueSet.add(key);
      }
    };

    for (const stored of collectOtherStoredValues(value, labelInValue)) {
      addExtra({value: stored, label: labelFromOtherStored(stored)});
    }

    for (const opt of lifecycleOtherOptions) {
      addExtra(opt);
    }

    const merged = [...base, ...extras];
    const hasSentinelOption = merged.some(
      (o) => String(o.value) === OTHER_OPTION_SENTINEL
    );
    setOptions(
      hasSentinelOption
        ? merged
        : [...merged, {value: OTHER_OPTION_SENTINEL, label: 'Specify Other'}]
    );
  }, [value, props.options, labelInValue, lifecycleOtherOptions]);

  useEffect(() => {
    if (!showOtherInput) {
      return;
    }
    queueMicrotask(() => {
      inputRef.current?.focus();
    });
  }, [showOtherInput]);

  const getEditingOtherStored = useCallback(() => {
    const isMultiple = mode === 'multiple' || mode === 'tags';

    return resolveEditingOtherStored(
      valueRef.current,
      lifecycleOtherOptionsRef.current,
      labelInValue,
      isMultiple
    );
  }, [labelInValue, mode]);

  const getOtherInputPrefill = useCallback(() => {
    const stored = getEditingOtherStored();

    return stored ? labelFromOtherStored(stored) : '';
  }, [getEditingOtherStored]);

  const handleChange = useCallback(
    (
      nextValue: SelectProps['value'],
      option?: Parameters<NonNullable<SelectProps['onChange']>>[1]
    ) => {
      if (isOtherSentinelSelectValue(nextValue, labelInValue)) {
        ignoreNextDropdownCloseRef.current = true;
        setOtherInputMaxLengthError(false);
        editingOtherStoredRef.current = getEditingOtherStored();
        setOtherInputDraft(getOtherInputPrefill());
        setShowOtherInput(true);
        const isMultiple = mode === 'multiple' || mode === 'tags';
        if (isMultiple) {
          const stripped = stripOtherSentinelFromArray(nextValue, labelInValue);
          if (
            !selectValuesShallowEqual(stripped, valueRef.current, labelInValue)
          ) {
            onChange?.(
              stripped,
              option as NonNullable<
                Parameters<NonNullable<SelectProps['onChange']>>[1]
              >
            );
          }
        }

        return;
      }

      setShowOtherInput(false);
      setOtherInputDraft('');
      setOtherInputMaxLengthError(false);
      onChange?.(
        normalizeClearedSelectValue(nextValue, mode),
        option as NonNullable<
          Parameters<NonNullable<SelectProps['onChange']>>[1]
        >
      );
    },
    [getEditingOtherStored, getOtherInputPrefill, labelInValue, mode, onChange]
  );

  const confirmOtherInput = useCallback(() => {
    if (otherInputDraft.length > maxLength) {
      return;
    }
    const trimmed = eachWordCap(otherInputDraft.trim());
    if (!trimmed) return;

    const stored = `${OTHER_VALUE_PREFIX}${trimmed}`;
    const newOption: OptionShape = {value: stored, label: trimmed};
    const isMultiple = mode === 'multiple' || mode === 'tags';
    const replaceStored = editingOtherStoredRef.current;
    const isReplacingEditedOther =
      !!replaceStored && replaceStored !== stored;

    setLifecycleOtherOption(
      newOption,
      isReplacingEditedOther ? replaceStored : undefined
    );
    editingOtherStoredRef.current = undefined;
    setOtherInputDraft('');
    setOtherInputMaxLengthError(false);

    const latest = valueRef.current;

    if (isMultiple) {
      if (labelInValue) {
        const prev = Array.isArray(latest) ? latest : [];
        const normalized: LabeledValue[] = prev.map((item) =>
          item && typeof item === 'object' && 'value' in item
            ? (item as LabeledValue)
            : {value: item as string | number, label: String(item)}
        );
        const withoutReplaced =
          isReplacingEditedOther && replaceStored
            ? normalized.filter(
                (item) => String(item.value) !== replaceStored
              )
            : normalized;
        const next = [...withoutReplaced, {value: stored, label: trimmed}];
        valueRef.current = next;
        onChange?.(next, newOption);
      } else {
        const prevValues = Array.isArray(latest)
          ? latest.map((v) =>
              v && typeof v === 'object' && 'value' in v
                ? (v as LabeledValue).value
                : v
            )
          : latest !== undefined && latest !== null
            ? [
                latest && typeof latest === 'object' && 'value' in latest
                  ? (latest as LabeledValue).value
                  : latest
              ]
            : [];
        const withoutReplaced =
          isReplacingEditedOther && replaceStored
            ? prevValues.filter((v) => String(v) !== replaceStored)
            : prevValues;
        const nextValues = [...withoutReplaced, stored];
        valueRef.current = nextValues;
        onChange?.(nextValues, newOption);
      }
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    } else if (labelInValue) {
      const labeledValue: LabeledValue = {
        value: stored,
        label: trimmed
      };
      onChange?.(labeledValue, newOption);
      queueMicrotask(() => {
        closeDropdown();
        selectRef.current?.blur();
      });
    } else {
      onChange?.(stored, newOption);
      queueMicrotask(() => {
        closeDropdown();
        selectRef.current?.blur();
      });
    }
  }, [
    closeDropdown,
    labelInValue,
    maxLength,
    mode,
    onChange,
    otherInputDraft,
    setLifecycleOtherOption
  ]);

  const addItem = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    confirmOtherInput();
  };

  const handleOtherInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const next = e.target.value;
    setOtherInputDraft(next);
    setOtherInputMaxLengthError(next.length > maxLength);
  };

  const handleOtherInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      addItem(e);
    }
  };

  return (
    <Select
      {...restProps}
      ref={selectRef}
      mode={mode}
      labelInValue={labelInValue}
      value={value}
      open={mergedOpen}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      onChange={handleChange}
      options={options}
      dropdownRender={(menu) => (
        <>
          {menu}
          {showOtherInput ? (
            /* Keep focus in the footer input and allow full multi-word typing (antd dropdownRender pattern). */
            <div
              onMouseDown={(e) => {
                const t = e.target as HTMLElement;
                // Let footer field focus; span.ant-input-affix-wrapper is the hit target for most clicks.
                if (
                  t.closest(
                    'label, input, textarea, [contenteditable="true"], .ant-input-affix-wrapper'
                  )
                ) {
                  return;
                }
                e.preventDefault();
              }}
              style={{width: '100%', boxSizing: 'border-box'}}
            >
              <Divider style={{margin: '8px 0'}} />
              <div
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '0 8px 8px'
                }}
              >
                <label
                  style={{
                    display: 'block',
                    width: '100%',
                    cursor: 'pointer',
                    margin: 0
                  }}
                >
                  <NoFormStyle status>
                    <Input
                      placeholder="Specify other..."
                      ref={inputRef}
                      value={otherInputDraft}
                      status={otherInputMaxLengthError ? 'error' : undefined}
                      allowClear={false}
                      style={{width: '100%'}}
                      onChange={handleOtherInputChange}
                      onKeyDown={handleOtherInputKeyDown}
                      suffix={
                        <Button
                          type="text"
                          size="small"
                          icon={<CheckOutlined />}
                          aria-label="Add custom option"
                          disabled={
                            !otherInputDraft.trim() ||
                            otherInputDraft.length > maxLength
                          }
                          onMouseDown={(e) => {
                            e.preventDefault();
                          }}
                          onClick={confirmOtherInput}
                        />
                      }
                    />
                    {otherInputMaxLengthError ? (
                      <div
                        style={{
                          color: '#ff4d4f',
                          fontSize: 12,
                          lineHeight: '20px',
                          marginTop: 4
                        }}
                      >
                        Maximum {maxLength} characters allowed
                      </div>
                    ) : null}
                  </NoFormStyle>
                </label>
              </div>
            </div>
          ) : null}
        </>
      )}
    />
  );
};

export default SelectWithOtherOption;
